import { Card } from 'primereact/card'
import { DataTable } from 'primereact/datatable'
import { Column as TableColumn } from 'primereact/column'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useCalibrationRodContext } from '../contexts/CalibrationRodContext'
import { useUserContext } from '../contexts/UserContext'
import TablePagination from '../table/TablePagination'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import ContextHeader from '../common/ContextHeader'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'
import { getSortableColumnProps, getSortableTableProps } from '../table/table-props'
import { TagIdCopyButton } from '../common/pure/CopyButton'
import CalibrationRod from '../../models/CalibrationRod'
import { mm } from '../../tools/format'
import t, { _tf } from '../../tools/t'

export default function AllCalibrationRodsScreen() {
    const { calibrationRods, archiveCalibrationRod, restoreCalibrationRod } =
        useCalibrationRodContext()
    const { me } = useUserContext()
    const canWriteCalibrationRods =
        !!me?.hasAdminPermission(PermissionSuffix.HardwareWrite) ||
        !!me?.hasAdminPermission(PermissionSuffix.DeviceApp)

    const onArchiveCalibrationRod = async (calibrationRod: CalibrationRod) => {
        if (
            !confirm(
                'Are you sure you want to archive calibration rod:\n' + calibrationRod.serialNo
            )
        )
            return
        await archiveCalibrationRod(calibrationRod.id)
    }

    const onRestoreCalibrationRod = async (calibrationRod: CalibrationRod) => {
        await restoreCalibrationRod(calibrationRod.id)
    }

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <ContextHeader>
                        <Heading standalone size={2}>
                            {t('calibration_rods_list')}
                        </Heading>
                    </ContextHeader>
                    <Card>
                        <DataTable
                            value={calibrationRods.items}
                            rowClassName={(c: CalibrationRod) =>
                                c.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            // onRowClick={navigateToBolt}
                            {...getSortableTableProps(calibrationRods)}
                        >
                            <TableColumn
                                header={t('common_tag')}
                                body={(c: CalibrationRod) => <TagIdCopyButton tag={c.currentTag} />}
                            />

                            <TableColumn
                                header={t('calibration_rod_field_serial')}
                                body={(c: CalibrationRod) => c.serialNo}
                            />

                            <TableColumn
                                header={t('calibration_rod_field_length')}
                                body={(c: CalibrationRod) => mm(c.apxTotalLength)}
                            />

                            <TableColumn
                                header={t('calibration_rod_field_material')}
                                body={(c: CalibrationRod) => _tf('', 'material', c.material)}
                            />

                            <TableColumn
                                header={t('life_cycle_field_created_at')}
                                body={(c: CalibrationRod) => (
                                    <DateDisplay value={c.createdAt} target={c.id} />
                                )}
                                {...getSortableColumnProps('createdAt')}
                            />

                            {canWriteCalibrationRods && (
                                <TableColumn
                                    body={(c: CalibrationRod) => (
                                        <TableActionContainer>
                                            {c.isArchived ? (
                                                <RestoreButton
                                                    target={c.id}
                                                    onClick={() => onRestoreCalibrationRod(c)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={c.id}
                                                    onClick={() => onArchiveCalibrationRod(c)}
                                                />
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={calibrationRods} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
